import axios from 'axios'
import type { LoginRequest, LoginResponse, User, ValidateTokenResponse } from '@/types/auth.types'

const TOKEN_KEY = 'auth_token'
const USER_KEY = 'auth_user'
const LAST_ACTIVITY_KEY = 'last_activity'
const INACTIVITY_TIMEOUT = 60 * 60 * 1000 // 1 ora in millisecondi

/**
 * Servizio per la gestione dell'autenticazione
 */
class AuthService {
  private baseURL = '/api/auth'
  private inactivityTimer: number | null = null

  /**
   * Effettua il login
   */
  async login(username: string, password: string): Promise<User> {
    try {
      const request: LoginRequest = { username, password }
      const response = await axios.post<LoginResponse>(`${this.baseURL}/login`, request)

      const user: User = {
        username: response.data.username,
        token: response.data.token,
        expiresAt: new Date(response.data.expiresAt)
      }

      // Salva in sessionStorage
      this.setToken(user.token)
      this.setUser(user)

      // Avvia il timer di inattività
      this.startInactivityTimer()

      return user
    } catch (error: any) {
      console.error('Errore durante il login:', error)
      throw new Error(error.response?.data?.message || 'Errore durante il login')
    }
  }

  /**
   * Effettua il logout
   */
  logout(): void {
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(USER_KEY)
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    this.stopInactivityTimer()
  }

  /**
   * Verifica se l'utente è autenticato
   */
  isAuthenticated(): boolean {
    const token = this.getToken()
    const user = this.getUser()

    if (!token || !user) {
      return false
    }

    // Verifica se il token è scaduto
    if (user.expiresAt && new Date(user.expiresAt) < new Date()) {
      this.logout()
      return false
    }

    return true
  }

  /**
   * Ottiene il token JWT dal sessionStorage
   */
  getToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY)
  }

  /**
   * Salva il token JWT nel sessionStorage (si cancella alla chiusura della finestra)
   */
  private setToken(token: string): void {
    sessionStorage.setItem(TOKEN_KEY, token)
  }

  /**
   * Ottiene l'utente dal sessionStorage
   */
  getUser(): User | null {
    const userJson = sessionStorage.getItem(USER_KEY)
    if (!userJson) return null

    try {
      const user = JSON.parse(userJson)
      // Converte la stringa della data in oggetto Date
      if (user.expiresAt) {
        user.expiresAt = new Date(user.expiresAt)
      }
      return user
    } catch {
      return null
    }
  }

  /**
   * Salva l'utente nel sessionStorage (si cancella alla chiusura della finestra)
   */
  private setUser(user: User): void {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user))
  }

  /**
   * Valida il token corrente
   */
  async validateToken(): Promise<ValidateTokenResponse> {
    try {
      const token = this.getToken()
      if (!token) {
        return { isValid: false, message: 'Token mancante' }
      }

      const response = await axios.get<ValidateTokenResponse>(`${this.baseURL}/validate`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return response.data
    } catch (error: any) {
      console.error('Errore durante la validazione del token:', error)
      return {
        isValid: false,
        message: error.response?.data?.message || 'Token non valido'
      }
    }
  }

  /**
   * Aggiorna il timestamp dell'ultima attività
   */
  private updateActivity(): void {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
  }

  /**
   * Controlla se l'utente è inattivo da più di 1 ora
   */
  private checkInactivity(): void {
    if (!this.isAuthenticated()) {
      return
    }

    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (!lastActivity) {
      this.updateActivity()
      return
    }

    const timeSinceLastActivity = Date.now() - parseInt(lastActivity)

    if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
      console.warn('Logout automatico per inattività (1 ora)')
      this.logout()
      window.location.href = '/login?message=session_expired'
    }
  }

  /**
   * Avvia il timer per il controllo dell'inattività
   */
  startInactivityTimer(): void {
    this.stopInactivityTimer()
    this.updateActivity()

    // Controlla ogni minuto se l'utente è inattivo
    this.inactivityTimer = window.setInterval(() => {
      this.checkInactivity()
    }, 60000) // Ogni 60 secondi

    // Imposta i listener per tracciare l'attività
    this.setupActivityListeners()
  }

  /**
   * Ferma il timer dell'inattività
   */
  private stopInactivityTimer(): void {
    if (this.inactivityTimer !== null) {
      clearInterval(this.inactivityTimer)
      this.inactivityTimer = null
    }
    this.removeActivityListeners()
  }

  /**
   * Imposta i listener per tracciare l'attività dell'utente
   */
  private setupActivityListeners(): void {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(event => {
      document.addEventListener(event, this.handleActivity, true)
    })
  }

  /**
   * Rimuove i listener per l'attività
   */
  private removeActivityListeners(): void {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true)
    })
  }

  /**
   * Handler per gli eventi di attività
   */
  private handleActivity = (): void => {
    this.updateActivity()
  }
}

export default new AuthService()
